import { useMemo, useState } from 'react';
import useCheckbox from 'hooks/useCheckbox';
import { nPayAvailableAirlines } from 'list/domestic/filter';
import { SeatClass } from 'list/domestic/types';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import { IRange } from 'list/common/filter/PriceRange';
import { useFilterSaveCookies } from './useFilterSave';
import { SearchType } from 'contexts/searchParams';
import { ARRIVAL, DEPARTURE, IFilterOptions } from 'list/domestic/utils';
import sessionStorage from 'storage/domesticFilter';

export enum FilterBy {
  SEAT_CLASS = 'seatClass',
  AIRLINE = 'airlineCode',
  DEPARTURE_TIME = 'departureTime',
  PRICE_RANGE = 'minFare',
  N_PAY = 'supportNPay',
}

export interface IFilters {
  [FilterBy.SEAT_CLASS]: Set<SeatClass>;
  [FilterBy.AIRLINE]: Set<string>;
  [FilterBy.DEPARTURE_TIME]: [Set<DepartureTime>, Set<DepartureTime>];
  [FilterBy.PRICE_RANGE]?: IRange;
}

const defaultFilters: IFilters = {
  [FilterBy.SEAT_CLASS]: new Set(),
  [FilterBy.AIRLINE]: new Set(),
  [FilterBy.DEPARTURE_TIME]: [new Set(), new Set()],
};

function isSameSet(aSet: Set<string>, bSet: Set<string>) {
  if (aSet.size !== bSet.size) return false;
  return Array.from(aSet).every(value => bSet.has(value));
}

export default function useFilters(filters = defaultFilters) {
  const [filterSave, toggleFilterSave] = useFilterSaveCookies(
    false,
    SearchType.DOMESTIC,
  );

  const [departure, arrival] = filters[FilterBy.DEPARTURE_TIME];
  const [
    seatClassFilters,
    resetSeatClassFilters,
    setSeatClassFilters,
    toggleSeatClassFilter,
  ] = useCheckbox<SeatClass>(filters[FilterBy.SEAT_CLASS]);
  const [
    airlineFilters,
    resetAirlineFilters,
    setAirlineFilters,
    toggleAirlineFilter,
    handleAirlineMultiSelect,
  ] = useCheckbox<string>(filters[FilterBy.AIRLINE]);
  const [
    timeFiltersForDeparture,
    resetTimeFiltersForDeparture,
    setTimeFiltersForDeparture,
    toggleTimeFiltersForDeparture,
  ] = useCheckbox<DepartureTime>(departure);
  const [
    timeFiltersForArrival,
    resetTimeFiltersForArrival,
    setTimeFiltersForArrival,
    toggleTimeFiltersForArrival,
  ] = useCheckbox<DepartureTime>(arrival);
  const [priceRange, setPriceRange] = useState<IRange | undefined>(
    filters[FilterBy.PRICE_RANGE],
  );
  const newFilters = useMemo<IFilters>(
    () => ({
      [FilterBy.SEAT_CLASS]: seatClassFilters,
      [FilterBy.AIRLINE]: airlineFilters,
      [FilterBy.DEPARTURE_TIME]: [
        timeFiltersForDeparture,
        timeFiltersForArrival,
      ],
      [FilterBy.PRICE_RANGE]: priceRange,
    }),
    [
      seatClassFilters,
      airlineFilters,
      timeFiltersForDeparture,
      timeFiltersForArrival,
      priceRange,
    ],
  );

  const isFiltered = useMemo<boolean>(
    () =>
      seatClassFilters.size > 0 ||
      airlineFilters.size > 0 ||
      timeFiltersForDeparture.size > 0 ||
      timeFiltersForArrival.size > 0 ||
      !!priceRange,
    [
      airlineFilters.size,
      priceRange,
      seatClassFilters.size,
      timeFiltersForArrival.size,
      timeFiltersForDeparture.size,
    ],
  );
  const supportNPay = isSameSet(airlineFilters, nPayAvailableAirlines);
  const toggleSupportNPay = () => {
    if (supportNPay) resetAirlineFilters();
    else setAirlineFilters(nPayAvailableAirlines);
  };

  function toggleToSave() {
    sessionStorage.clean();
    toggleFilterSave();
  }

  const setFilters = (options?: IFilterOptions) => (newFilters: IFilters) => {
    const { seatClass, airlineCode, departureTime, minFare } = newFilters;
    setSeatClassFilters(newFilters[FilterBy.SEAT_CLASS]);
    setAirlineFilters(newFilters[FilterBy.AIRLINE]);
    setTimeFiltersForDeparture(newFilters[FilterBy.DEPARTURE_TIME][DEPARTURE]);
    setTimeFiltersForArrival(newFilters[FilterBy.DEPARTURE_TIME][ARRIVAL]);
    setPriceRange(newFilters[FilterBy.PRICE_RANGE]);
    if (filterSave) {
      sessionStorage.set({
        seatClass: Array.from(seatClass),
        airlineCode: Array.from(airlineCode).reduce(
          (obj, code) => ({ ...obj, [code]: options?.airlines.get(code) }),
          {},
        ),
        departureTime: departureTime.map(el => Array.from(el)),
        minFare,
      });
    }
    // reload();
  };

  const resetAllFilters = () => {
    resetSeatClassFilters();
    resetAirlineFilters();
    resetTimeFiltersForDeparture();
    resetTimeFiltersForArrival();
    setPriceRange(undefined);

    sessionStorage.clean();
  };

  return {
    seatClassFilters,
    resetSeatClassFilters,
    setSeatClassFilters,
    toggleSeatClassFilter,
    airlineFilters,
    resetAirlineFilters,
    setAirlineFilters,
    toggleAirlineFilter,
    handleAirlineMultiSelect,
    timeFiltersForDeparture,
    resetTimeFiltersForDeparture,
    setTimeFiltersForDeparture,
    toggleTimeFiltersForDeparture,
    timeFiltersForArrival,
    resetTimeFiltersForArrival,
    setTimeFiltersForArrival,
    toggleTimeFiltersForArrival,
    priceRange,
    setPriceRange,
    supportNPay,
    toggleSupportNPay,
    filters: newFilters,
    resetAllFilters,
    setFilters,
    filterSave,
    toggleToSave,
    isFiltered,
  };
}
